// Hooks
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import useLogin from "@common/components/hooks/useLogin";
import useQrCode from "@common/components/hooks/useQrCode";

// Actions
import AgcCheckAction from "@common/redux/actions/AgcCheckAction";
import LoadingAction from "@common/redux/actions/LoadingAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";

// API
import AgcCheckClient from "@common/api/AgcCheckClient";

// Helpers
import { handleError } from "@common/components/helpers/error";
import { parseAgcCheckResponse } from "@common/components/helpers/compliance";
import { convertQuestionsToResponse } from "../helpers/AgcCheck/parseAgcCheckQuestions";
import { getCurrentDateTime } from "../helpers/datetime";

const useAgcCheck = () => {
  const data = useSelector((state) => AgcCheckAction.getStatePath(state));

  const [{ isDesktop, name, site }] = useLogin();

  const { locations } = useQrCode();

  const { scanResult, questions, timeChecked } = data;

  const setState = (data) => AgcCheckAction.dispatch(data);

  const history = useHistory();

  const reset = () => {
    setState({
      scanResult: null,
      stopStream: false,
      questions: questions.map((question) => ({
        ...question,
        value: question.id === 5 ? 0 : null,
      })),
    });
  };

  const onBack = () => {
    history.push("/compliance");
    reset();
  };

  const showScanner = () => setState({ scanResult: null, stopStream: false });

  const onScan = async (err, result) => {
    if (err) return;
    if (result) {
      const scanResult = JSON.parse(result.text);
      setState({
        scanResult,
        stopStream: true,
        timeChecked: getCurrentDateTime(),
      });

      if (site && scanResult?.siteCode !== site.siteCode) {
        AlertModalAction.open(
          "Invalid QR code, please scan the QR code linked with your site.",
          "Warning",
          history.push("/compliance"),
        );
        reset();
      }
    } else setState({ scanResult: null });
  };

  const handleAnswers = (id, value) => {
    setState({
      questions: questions.map(
        (question) =>
          question.id === id
            ? { ...question, value: value } // Create a new object with the updated value
            : question, // Keep other questions unchanged
      ),
    });
  };

  const filteredQuestions = scanResult
    ? questions.filter((x) =>
        scanResult.location === locations[1].text ? x.id === 9 : x.id !== 9,
      )
    : questions;

  const onSubmit = () => {
    if (!scanResult) return;

    const fnSubmit = isDesktop ? AgcCheckClient.Update : AgcCheckClient.Create;

    const payload = {
      ...(isDesktop ? data : {}),
      ...(isDesktop ? convertQuestionsToResponse(questions) : { questions }),
      siteCode: scanResult.siteCode,
      location: scanResult.location,
      timeChecked,
    };

    LoadingAction.set(true).then(() =>
      fnSubmit(payload)
        .then((response) => {
          AlertModalAction.open(
            "AGC Check record created successfully!",
            "Success",
            isDesktop ? () => {} : () => history.push("/compliance"),
          );

          isDesktop
            ? setState(parseAgcCheckResponse(response, questions))
            : reset();
        })
        .catch((error) => handleError(error))
        .finally(() => {
          LoadingAction.set(false);
        }),
    );
  };

  return {
    ...data,
    setState,
    isDesktop,
    name,
    filteredQuestions,
    showScanner,
    onBack,
    onScan,
    handleAnswers,
    onSubmit,
  };
};

export default useAgcCheck;
